import { SERVICE_MASTER_STATUS, TOwnerAutoSuggestService } from "@elixir/types";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { putUpdateServicesMaster } from "apis";
import { AxonGenericApiResponse } from "apis/src/types/axon";
import { getServicesMasterAutosuggestQuery } from "./use-get-services-master-autosuggest";
import { getServicesMasterInfiniteScrollQuery } from "./use-get-services-master-infinite-query";
import { useMyAccessProfileV2 } from "../../tryst/get-my-access-profile";

export default function usePutUpdateServicesMaster({
  query,
  clinic_id,
  status,
  searchInAllServiceTypes,
}: {
  query?: string;
  clinic_id: string;
  status: SERVICE_MASTER_STATUS;
  searchInAllServiceTypes: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: myAccessProfile } = useMyAccessProfileV2();

  return useMutation(putUpdateServicesMaster, {
    onMutate: ({ service_id, price, body }) => {
      queryClient.setQueryData<
        AxonGenericApiResponse<TOwnerAutoSuggestService[]>
      >(
        getServicesMasterAutosuggestQuery({
          query,
          owner_id: myAccessProfile?.business?.id!,
          clinic_id,
        }),
        (currServiceInfo) => {
          if (!currServiceInfo) return currServiceInfo;

          const updatedServiceInfo = currServiceInfo?.data
            ?.filter((service) => {
              if (
                service.price === price &&
                service.service_id === service_id
              ) {
                if (body.archive !== undefined) {
                  const newArchiveStatus = body.archive;
                  const shouldShowInCurrentFilter =
                    (status === SERVICE_MASTER_STATUS.ACTIVE &&
                      !newArchiveStatus) ||
                    (status === SERVICE_MASTER_STATUS.INACTIVE &&
                      newArchiveStatus) ||
                    searchInAllServiceTypes;

                  return shouldShowInCurrentFilter;
                }
                return true;
              }
              return true;
            })
            .map((service) => {
              if (
                service.price === price &&
                service.service_id === service_id
              ) {
                return {
                  ...service,
                  ...body,
                };
              }
              return service;
            });

          return { ...currServiceInfo, data: updatedServiceInfo };
        }
      );

      queryClient.setQueryData<
        InfiniteData<AxonGenericApiResponse<TOwnerAutoSuggestService[]>>
      >(
        getServicesMasterInfiniteScrollQuery({
          query,
          owner_id: myAccessProfile?.business?.id!,
          clinic_id,
          status: searchInAllServiceTypes ? undefined : status,
          searchInAllServiceTypes,
        }),
        (currServiceInfo) => {
          if (!currServiceInfo) return currServiceInfo;

          return {
            ...currServiceInfo,
            pages: currServiceInfo.pages.map((page) => {
              return {
                ...page,
                data: page.data
                  ?.filter((service) => {
                    if (
                      service.price === price &&
                      service.service_id === service_id
                    ) {
                      if (body.archive !== undefined) {
                        const newArchiveStatus = body.archive;
                        const shouldShowInCurrentFilter =
                          (status === SERVICE_MASTER_STATUS.ACTIVE &&
                            !newArchiveStatus) ||
                          (status === SERVICE_MASTER_STATUS.INACTIVE &&
                            newArchiveStatus) ||
                          searchInAllServiceTypes;

                        return shouldShowInCurrentFilter;
                      }
                      return true;
                    }
                    return true;
                  })
                  .map((service) => {
                    if (
                      service.price === price &&
                      service.service_id === service_id
                    ) {
                      return {
                        ...service,
                        ...body,
                      };
                    }
                    return service;
                  }),
              };
            }),
          };
        }
      );
    },
  });
}
